<template>
  <button
    v-if="!isCurrentUser"
    :disabled="loading || disabled"
    :class="buttonClasses"
    @click.stop.prevent="handleClick">
    <template v-if="variant === 'icon-only'">
      <fa-icon
        v-if="displayIcon && !loading"
        :icon="displayIcon"
        :class="iconClasses" />
    </template>
    <template v-else>
      <fa-icon
        v-if="displayIcon && !loading"
        :icon="displayIcon"
        :class="iconClasses" />
      <span>
        <slot>{{ displayText }}</slot>
      </span>
    </template>
    <fa-icon v-if="loading" icon="spinner-third" spin :class="iconClasses" />
  </button>

  <!-- Unfollow Confirmation Sheet -->
  <OptionsSheet v-model="showUnfollowConfirm" title="Unfollow">
    <div class="px-4 pb-2 text-sm text-gray-400">
      Are you sure you want to unfollow
      <span class="font-medium text-white">@{{ props.user?.username }}</span>?
    </div>
    <button
      type="button"
      class="flex items-center w-full gap-3 px-4 py-3 text-left transition-colors rounded-lg cursor-pointer text-red-400 hover:bg-white/10"
      @click="confirmUnfollow">
      <fa-icon :icon="['fas', 'user-minus']" class="w-5" />
      <span class="font-medium">Unfollow</span>
    </button>
    <button
      type="button"
      class="flex items-center w-full gap-3 px-4 py-3 text-left transition-colors rounded-lg cursor-pointer text-white hover:bg-white/10"
      @click="showUnfollowConfirm = false">
      <fa-icon :icon="['fas', 'xmark']" class="w-5" />
      <span class="font-medium">Cancel</span>
    </button>
  </OptionsSheet>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreProfile } from '@/stores/storeProfile'
import { useGuestActions } from '@/composables/useGuestActions'
import { OptionsSheet } from '@/components/Options'

const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  variant: {
    type: String,
    default: 'default', // 'default', 'compact', 'icon-only', 'header'
    validator: (value) =>
      ['default', 'compact', 'icon-only', 'header'].includes(value)
  },
  size: {
    type: String,
    default: 'md', // 'sm', 'md', 'lg'
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  text: {
    type: String,
    default: ''
  },
  icon: {
    type: [String, Array],
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  showOptimisticUI: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['follow', 'unfollow', 'error'])

const storeAuth = useStoreAuth()
const storeProfile = useStoreProfile()
const { requireAuthFor } = useGuestActions()

const userId = computed(() => props.user?.user_id)

const isCurrentUser = computed(() => {
  return storeAuth.user?.id === userId.value
})

// Optimistic UI state
const optimisticFollow = ref(false)
const optimisticUnfollow = ref(false)
const loading = ref(false)
const showUnfollowConfirm = ref(false)

// Actual following state from store
const actualIsFollowing = computed(() => {
  if (!userId.value) return false
  return storeProfile.isFollowingUser(userId.value)
})

// Computed following state (with optimistic UI)
const isFollowing = computed(() => {
  if (!userId.value) return false

  // If optimistic UI is disabled, only use actual state
  if (!props.showOptimisticUI) {
    return actualIsFollowing.value
  }

  // Optimistic UI: show optimistic state first, then actual state
  if (optimisticFollow.value) return true
  if (optimisticUnfollow.value) return false
  return actualIsFollowing.value
})

// Default icon based on following state
const defaultIcon = computed(() => {
  return isFollowing.value ? ['fas', 'user-check'] : ['fas', 'user-plus']
})

const displayIcon = computed(() => {
  if (props.icon) {
    // If icon is provided as prop, use it (can be string or array)
    return props.icon
  }
  // Otherwise use default icon based on following state
  return defaultIcon.value
})

// Default text based on following state
const defaultText = computed(() => {
  return isFollowing.value ? 'Following' : 'Follow'
})

const displayText = computed(() => {
  return props.text || defaultText.value
})

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  }
  return sizes[props.size] || sizes.md
})

const variantClasses = computed(() => {
  const baseClasses =
    'flex items-center justify-center font-medium transition-all duration-200 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100'

  if (props.variant === 'icon-only') {
    const focusRing = isFollowing.value
      ? 'focus-visible:ring-red-400'
      : 'focus-visible:ring-blue-400'
    return `${baseClasses} rounded-full p-2 hover:scale-110 active:scale-95 ${focusRing} ${
      isFollowing.value
        ? 'text-blue-400 hover:text-red-400 hover:bg-red-400/10'
        : 'text-white hover:text-blue-400 hover:bg-blue-400/10'
    }`
  }

  if (props.variant === 'compact') {
    const focusRing = isFollowing.value
      ? 'focus-visible:ring-red-400'
      : 'focus-visible:ring-blue-400'
    return `${baseClasses} rounded-full px-2 py-0.5 text-2xs hover:scale-105 active:scale-95 ${focusRing} ${
      isFollowing.value
        ? 'text-white bg-red-500/70 ring-1 ring-inset ring-red-400/50 hover:bg-red-500/90'
        : 'text-blue-400 bg-blue-400/10 ring-1 ring-inset ring-blue-400/30 hover:bg-blue-400/20'
    }`
  }

  if (props.variant === 'header') {
    // Header variant - matches the invite friends button style
    return `flex flex-1 gap-2 justify-center items-center px-4 py-2.5 max-w-xs text-sm font-medium rounded-xl transition-all cursor-pointer border-b-2 active:border-b-0 active:mt-0.5 disabled:opacity-50 disabled:cursor-not-allowed ${
      isFollowing.value
        ? 'text-white bg-white/20 border-white/30 hover:bg-red-500/50 hover:border-red-700/50'
        : 'text-white bg-white/10 border-white/20 hover:bg-white/20'
    }`
  }

  // Default variant
  const focusRing = isFollowing.value
    ? 'focus-visible:ring-purple-400'
    : 'focus-visible:ring-indigo-400'
  return `${baseClasses} rounded-lg gap-x-2 hover:scale-105 active:scale-95 ${focusRing} ${
    isFollowing.value
      ? 'text-white bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-red-400 hover:to-purple-400'
      : 'text-gray-700 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-blue-300 hover:to-indigo-400'
  }`
})

const buttonClasses = computed(() => {
  return [variantClasses.value, sizeClasses.value].filter(Boolean).join(' ')
})

const iconClasses = computed(() => {
  if (props.variant === 'icon-only') {
    return props.size === 'sm'
      ? 'text-base'
      : props.size === 'lg'
        ? 'text-2xl'
        : 'text-xl'
  }

  const sizeClass =
    props.size === 'sm'
      ? 'w-4 h-4'
      : props.size === 'lg'
        ? 'w-6 h-6'
        : 'w-5 h-5'

  // Add mr-0.5 spacing for compact variant (which doesn't use gap-x-2)
  if (props.variant === 'compact') {
    return `${sizeClass} mr-0.5`
  }

  return sizeClass
})

const handleClick = async (event) => {
  // Prevent event bubbling and default behavior
  event.stopPropagation()
  event.preventDefault()

  // If already following, show confirmation sheet instead of immediately unfollowing
  if (isFollowing.value) {
    showUnfollowConfirm.value = true
    return
  }

  // Check authentication first (for follow action)
  const shouldProceed = requireAuthFor.follow(
    async () => {
      await performFollowAction(true) // true = follow
    },
    {
      username: props.user?.username,
      displayname: props.user?.displayname,
      id: userId.value
    }
  )

  if (!shouldProceed) {
    // Auth prompt was shown, don't proceed
    return
  }
}

const confirmUnfollow = async () => {
  showUnfollowConfirm.value = false
  await performFollowAction(false) // false = unfollow
}

const performFollowAction = async (willFollow) => {
  if (loading.value) return

  // Set optimistic UI state
  if (props.showOptimisticUI) {
    optimisticFollow.value = willFollow
    optimisticUnfollow.value = !willFollow
  }

  loading.value = true

  try {
    let result
    if (willFollow) {
      result = await storeProfile.followUser(userId.value)
      emit('follow', { userId: userId.value, user: props.user })
    } else {
      result = await storeProfile.unfollowUser(userId.value)
      emit('unfollow', { userId: userId.value, user: props.user })
    }

    // Check if the action was successful
    if (!result || !result.success) {
      // Revert optimistic UI on error
      if (props.showOptimisticUI) {
        optimisticFollow.value = false
        optimisticUnfollow.value = false
      }
      emit('error', {
        userId: userId.value,
        user: props.user,
        action: willFollow ? 'follow' : 'unfollow',
        error: result?.error
      })
    }
  } catch (error) {
    // Revert optimistic UI on error
    if (props.showOptimisticUI) {
      optimisticFollow.value = false
      optimisticUnfollow.value = false
    }
    emit('error', {
      userId: userId.value,
      user: props.user,
      action: willFollow ? 'follow' : 'unfollow',
      error
    })
  } finally {
    loading.value = false
  }
}
</script>
